// <purpose>The footer letter check (2026-10-30): its own section above the button section, holding
// the check's split pills (services/letterCheck) or — once the row is decided — the verdict in the
// same slot. A pill opens a preview card of only its items: the mail's words beside the letter's
// nearest sentence. Picking a card row hands the pair to the host (compare mode opens at it) and
// closes the card; the pill stays filled until then. Registry: design/design-languages.html,
// entries `ai_descriptors` (footer pills) and `footer_check_card`.</purpose>
// <dependencies>services/letterCheck (pills + rows, pure), SplitPill (the one pill), theme.</dependencies>
// <constraints>Layout law: the section hugs its pills (no empty band) and its height never drops
// while the same row is open — the tallest height it has had for this row is its floor, so a card
// opening, the verdict replacing the pills or a filled field re-labelling its pill moves nothing.
// The card is a layer above the section, never in the flow. Never a tick or a cross in a card.</constraints>
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SplitPill } from './SplitPill';
import { cardRowLetterText, type CheckCardRow, type CheckPill } from '../services/letterCheck';
import { theme } from '../theme';

export const FOOTER_CHECK_MIN_HEIGHT = 40;

export interface LetterCheckFooterProps {
  /** null ⇒ the row has no letter to check (no pill section, unless a verdict stands). */
  pills: CheckPill[] | null;
  /** The decided row's verdict: takes the pills' slot. */
  verdict: React.ReactNode | null;
  /** The card's right column head: „Entwurf", „Antwort", „Weiterleitung". */
  letterWord: string;
  /** The row the pills belong to — a change closes the card and resets the height floor. */
  resetKey: string;
  /** The letter shown — a switch closes the card (its rows belong to the other letter). */
  letterKey?: string;
  onPickRow: (row: CheckCardRow, kind: CheckPill['kind']) => void;
  /** The pill whose card row opened compare mode: it stays filled until Esc or another pill. Held
   *  by the host, because this section remounts when the footer moves into the stage. */
  pinnedKind?: CheckPill['kind'] | null;
  /** Any pill pressed: the host lets go of the pinned one. */
  onPillPress?: () => void;
  /** The „Felder" pill: take the clerk to the first open field. */
  onFields?: () => void;
  /** The disabled Genehmigen is hovered: the „Felder" pill shows it is the cause. */
  fieldsHighlight?: boolean;
}

const miniButton: React.CSSProperties = {
  fontFamily: theme.font.family, fontSize: 11.5, fontWeight: 600, color: theme.colors.navyMid,
  backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: 5, padding: '1px 9px', cursor: 'pointer', lineHeight: '18px',
};

const CheckCard: React.FC<{ pill: CheckPill; letterWord: string; onPick: (row: CheckCardRow) => void; onClose: () => void }> = ({ pill, letterWord, onPick, onClose }) => {
  const title = `${pill.label} · ${pill.value}`;
  const firstRow = useRef<HTMLButtonElement>(null);
  useEffect(() => { firstRow.current?.focus({ preventScroll: true }); }, []);
  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid="check-card"
      style={{
        position: 'absolute', left: 12, right: 12, bottom: 'calc(100% + 6px)', zIndex: 4,
        display: 'flex', flexDirection: 'column', maxHeight: 250, boxSizing: 'border-box',
        backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.borderLight}`, borderRadius: 10,
        boxShadow: '0 -8px 24px rgba(0,13,102,0.14)', fontFamily: theme.font.family,
        animation: 'stx-cardRise 0.15s ease-out',
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px',
        borderBottom: `1px solid ${theme.colors.bgLight}`, fontSize: 11.5, fontWeight: 600, color: theme.colors.navy,
      }}>
        <span>{title}</span>
        <button type="button" onClick={onClose} style={miniButton}>Schließen (Esc)</button>
      </div>
      <div aria-hidden="true" style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '6px 14px 0',
        fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.colors.textSecondary, fontWeight: 600,
      }}>
        <span>Mail</span><span>{letterWord}</span>
      </div>
      <div style={{ overflowY: 'auto', padding: '4px 14px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {pill.rows.map((row, n) => {
          const right = cardRowLetterText(row);
          return (
            <button
              key={`${row.claimIndex}-${n}`}
              ref={n === 0 ? firstRow : undefined}
              type="button"
              data-testid="check-card-row"
              onClick={() => onPick(row)}
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, textAlign: 'left',
                fontFamily: theme.font.family, fontSize: 12, lineHeight: 1.45, color: theme.colors.navy,
                backgroundColor: theme.colors.white, border: '1px solid transparent', borderRadius: 6,
                padding: '6px 8px', margin: '0 -8px', cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = theme.colors.borderLight; e.currentTarget.style.backgroundColor = '#f7f8fc'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.backgroundColor = theme.colors.white; }}
            >
              <span>{row.mail === row.mailQuote ? `„${row.mail}“` : row.mail}</span>
              <span style={{ color: right.muted ? theme.colors.textSecondary : undefined }}>{right.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const LetterCheckFooter: React.FC<LetterCheckFooterProps> = ({
  pills, verdict, letterWord, resetKey, letterKey, onPickRow, onFields, fieldsHighlight, pinnedKind, onPillPress,
}) => {
  const [openKind, setOpenKind] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  // The floor: the tallest this section has been for this row (reset with the row).
  const [floor, setFloor] = useState<{ key: string; h: number }>({ key: resetKey, h: 0 });
  const minHeight = floor.key === resetKey ? floor.h : 0;
  useLayoutEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    if (floor.key !== resetKey || h > floor.h) setFloor({ key: resetKey, h });
  }, [floor, resetKey, pills, verdict]);
  useEffect(() => { setOpenKind(null); }, [resetKey, letterKey]);

  const openPill = openKind && !verdict ? pills?.find(p => p.kind === openKind) ?? null : null;
  useEffect(() => {
    if (!openPill) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation(); setOpenKind(null);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [openPill]);

  if (!verdict && !pills) return null;
  return (
    <div
      ref={sectionRef}
      data-testid="footer-check"
      style={{
        position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
        padding: '9px 0', boxSizing: 'border-box',
        minHeight: Math.max(FOOTER_CHECK_MIN_HEIGHT, minHeight),
      }}
    >
      {openPill && openPill.rows.length > 0 && (
        <CheckCard
          pill={openPill}
          letterWord={letterWord}
          onClose={() => setOpenKind(null)}
          onPick={row => { const kind = openPill.kind; setOpenKind(null); onPickRow(row, kind); }}
        />
      )}
      {verdict ? (
        <div data-testid="footer-verdict" style={{ display: 'flex', alignItems: 'center', gap: 10, animation: 'stx-fadeIn 0.15s ease' }}>
          {verdict}
        </div>
      ) : pills!.map(p => {
        const opensCard = p.rows.length > 0;
        const isFields = p.kind === 'fields';
        const pressable = opensCard || (isFields && !!onFields);
        return (
          <SplitPill
            key={p.kind}
            variant="check"
            label={p.label}
            testId={`check-pill-${p.kind}`}
            ariaLabel={p.ariaLabel}
            quiet={!pressable}
            pressed={openKind ? openKind === p.kind : pinnedKind === p.kind}
            onClick={pressable ? () => {
              onPillPress?.();
              if (isFields) { onFields?.(); return; }
              setOpenKind(k => (k === p.kind ? null : p.kind));
            } : undefined}
            style={{
              animation: 'stx-fadeIn 0.15s ease',
              ...(isFields ? { minWidth: 140, justifyContent: 'flex-start' } : null),
              ...(isFields && fieldsHighlight ? { boxShadow: `0 0 0 0.5px ${theme.colors.navy}`, borderColor: theme.colors.navy } : null),
            }}
          >
            {p.value}
          </SplitPill>
        );
      })}
    </div>
  );
};

export default LetterCheckFooter;
